import { type UIAlertProps, PlayerAccountClient } from '../types'
import type { RouteLocationNormalizedGeneric } from '#vue-router'
import { toValue, navigateTo, useNotifications, useDayjs, useUserAuthClient, useCoreConfig, useUserInfo } from '#imports'

interface CheckResultContext {
    ends_at?: string
    redirectUrl?: string
    [key: string]: unknown
}

/**
 * Account-restriction statuses where failing open is unacceptable. If the
 * backend reports one of these but the consuming app forgot to configure it in
 * `guards.statuses`, the user must NOT be silently allowed through — that would let e.g. a
 * deactivated user reach the deposit page with no notice.
 * For these, the guard fails safe (warn + redirect to the frontpage) instead.
 */
const CRITICAL_STATUSES = new Set<string>([
    PlayerAccountClient.UserCheckResultType.deactivated,
    PlayerAccountClient.UserCheckResultType.block,
    PlayerAccountClient.UserCheckResultType.suspended,
    PlayerAccountClient.UserCheckResultType.suspended_missing_sof,
    PlayerAccountClient.UserCheckResultType.expired_block_must_be_removed_by_customer_service,
    PlayerAccountClient.UserCheckResultType.cooldown,
])

export const useGuard = () => {
    const userInfoQuery = useUserInfo({
        lazy: false,
        server: true
    })
    const { data, isLoggedIn } = userInfoQuery
    const { getLoginLimitsCheck } = useUserAuthClient()
    const { guards, site } = useCoreConfig()
    const dayjs = useDayjs()
    const { add: addNotification } = useNotifications()

    const handleNotification = (alert: UIAlertProps, status?: PlayerAccountClient.components['schemas']['UserInfoResponse']['checkResult']) => {
        if (!status) return

        let ends_at: string | undefined = undefined
        const context = status.context as unknown as CheckResultContext | undefined

        switch (status.type) {
            case PlayerAccountClient.UserCheckResultType.block:
                ends_at = context?.ends_at
                break

            case PlayerAccountClient.UserCheckResultType.cooldown:
                ends_at = context?.ends_at
                break
        }

        if (ends_at) {
            const formattedEndtime = dayjs(ends_at).tz().format('DD/MM/YYYY HH:mm:ss')
            const alertMessage = alert?.message?.replace('{date}', formattedEndtime)

            addNotification({
                type: status.type,
                alert: {
                    ...alert,
                    message: alertMessage,
                },
            })

            return
        }

        addNotification({
            type: status.type,
            alert,
        })
    }

    const can = async (route?: RouteLocationNormalizedGeneric) => {
        if (!isLoggedIn.value) return true

        // Allow visiting allowed routes regardless of status. These are the
        // safe-harbor routes a restricted (e.g. deactivated) user is deliberately
        // permitted to reach.
        if (route && guards.allowedRoutes.includes(route.name)) return true

        let statusRaw = toValue(data.value?.checkResult)

        if (!statusRaw) {
            try {
                const { data: loginLimitsData } = await getLoginLimitsCheck()

                if (loginLimitsData.value?.data?.withinLimits === false) {
                    statusRaw = {
                        type: 'reached-play-limits' as PlayerAccountClient.UserCheckResultType,
                        context: {
                            redirectUrl: '/users/reached-play-limits'
                        }
                    }
                }
                else {
                    return true
                }
            }
            catch {
                return true
            }
        }

        const statusObject = statusRaw?.type ? guards.statuses?.[statusRaw.type] : undefined

        if (!statusObject) {
            // Fail safe: the backend reported a critical account restriction that
            // the app forgot to configure. Failing open here would silently expose
            // sensitive pages (e.g. deposit). Warn loudly and redirect to the
            // frontpage, unless we're already somewhere safe.
            if (statusRaw?.type && CRITICAL_STATUSES.has(statusRaw.type)) {
                console.warn(
                    `[useGuard] User has critical status "${statusRaw.type}" but it is not configured in guards.statuses. `
                    + 'Redirecting to the frontpage as a fail-safe — add this status to guards.statuses.'
                )

                const frontpage = site?.frontpageUrl ?? '/'

                // allowedRoutes are already handled above; only guard against a
                // redirect loop on the frontpage itself.
                if (route?.fullPath === frontpage || route?.name === frontpage) {
                    return true
                }

                if (route) return navigateTo(frontpage)

                await navigateTo(frontpage)

                return false
            }

            return true
        }

        const redirectTarget = statusObject?.route
        const alert = statusObject?.alert
        const targetPath = (redirectTarget as RouteLocationNormalizedGeneric)?.name || redirectTarget

        // Already on the correct page, so we allow the navigation to complete.
        if (route?.fullPath === targetPath || route?.name === targetPath) {
            return true
        }

        if (alert) {
            handleNotification(alert, statusRaw)
        }

        // Don't allow extra allowed routes, if the user status has forced redirect
        if (statusObject.isForcedRedirect) {
            if (route) return navigateTo(redirectTarget)

            await navigateTo(redirectTarget)

            return false
        }

        if (route && guards.extraAllowedRoutes.includes(route.name)) return true

        if (route) return navigateTo(redirectTarget)

        await navigateTo(redirectTarget)

        return false
    }

    return userInfoQuery.enrich({
        can
    })
}
